import { tokenManager } from '../auth/tokenManager';
import { logger } from '../../utils/logger';
import { API_BASE_URL } from '../../constants/apiConstants';

/**
 * SSE event for ASN ingest batch progress. Mirrors backend
 * `IngestProgressEvent` (Domain/PackAnalysis/Messages). Fields are camelCase
 * because the backend's JsonSerializerOptions sets `PropertyNamingPolicy =
 * JsonNamingPolicy.CamelCase`.
 */
export interface IngestProgressEvent {
  eventType: 'batch_started' | 'file_started' | 'file_completed' | 'batch_completed';
  clientBatchId: string;
  at: string;
  totalFiles: number;
  fileIndex: number;
  fileName?: string | null;
  doc856Id?: number | null;
  statusCode?: number | null;
  statusText?: string | null;
  elapsedMs?: number | null;
}

export interface SubscribeOptions {
  onEvent: (evt: IngestProgressEvent) => void;
  onError?: (err: unknown) => void;
  signal?: AbortSignal;
}

/**
 * Opens an SSE stream at `/api/v1/pack-analysis/asn/ingest/progress/{clientBatchId}`
 * and dispatches each event to `onEvent`. Resolves when the server closes the stream
 * (after `batch_completed`) or rejects if connection setup fails.
 *
 * Why fetch + ReadableStream instead of EventSource:
 *   EventSource doesn't support custom headers, so attaching the JWT bearer would
 *   force `?token=` in the URL — security smell (logged in proxy access logs).
 *   fetch + ReadableStream lets us use the standard Authorization header.
 *
 * Wire format: per the SSE spec the server emits one frame as:
 *   `data: {json}\n\n`
 * Multi-line frames concatenate with `\n` between `data:` lines; we split on
 * blank lines (`\n\n`) and parse each frame's accumulated `data` payload.
 */
export async function subscribeToIngestProgress(
  clientBatchId: string,
  { onEvent, onError, signal }: SubscribeOptions
): Promise<void> {
  const tokens = tokenManager.getTokens();
  const accessToken = tokens?.accessToken;
  if (!accessToken) {
    throw new Error('No access token — cannot open SSE subscription.');
  }

  // Hit the backend origin directly. A bare `/api/...` path would resolve against
  // the Next.js dev origin (localhost:3000) — and `trailingSlash: true` in
  // next.config.mjs would then redirect to `…/{id}/` and 404, since Next.js has
  // no route for the API. Mirror what apiClient does (baseURL = API_BASE_URL).
  const base = API_BASE_URL.replace(/\/+$/, '');
  const url = `${base}/api/v1/pack-analysis/asn/ingest/progress/${encodeURIComponent(clientBatchId)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'text/event-stream',
    },
    signal,
  });

  if (!response.ok || !response.body) {
    throw new Error(`SSE subscribe failed: HTTP ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Split on \n\n boundary; keep the trailing partial frame in the buffer.
      let boundary;
      while ((boundary = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);

        // Each frame is N lines; we only care about `data:` lines (server may
        // also emit `: keepalive` comments that we ignore here).
        const dataLines: string[] = [];
        for (const line of frame.split('\n')) {
          if (line.startsWith('data:')) {
            // Per SSE spec, strip the `data:` prefix + optional single leading space.
            dataLines.push(line.slice(line.startsWith('data: ') ? 6 : 5));
          }
        }
        if (dataLines.length === 0) continue;

        const payload = dataLines.join('\n');
        try {
          const evt = JSON.parse(payload) as IngestProgressEvent;
          onEvent(evt);
        } catch (parseErr) {
          logger.warn('SSE: dropped malformed frame', { payload, parseErr });
        }
      }
    }
  } catch (err) {
    // AbortError (signal aborted) is a normal exit — don't escalate.
    if ((err as { name?: string })?.name === 'AbortError') return;
    if (onError) onError(err);
    throw err;
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}
